/** Briefing do cliente — só a Lara edita depois de enviado. Fonte: docs/00G §4. */
import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import { Link, useParams } from "react-router";
import { BRIEFING_SCHEMA } from "@unik/shared";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Card, Skeleton } from "../../ui/Card";
import { Input, TextArea } from "../../ui/Input";
import { Select } from "../../ui/Select";
import {
  STRATEGIC_BRIEFING_CHAPTERS,
  getBriefingValue,
  FIELD_ALIASES,
} from "../../lib/briefingResolver";

interface BriefingDTO {
  responses: Record<string, string>;
  status: "draft" | "submitted";
  submittedAt: string | null;
}

export function BriefingView() {
  const { id } = useParams();
  const [data, setData] = useState<BriefingDTO | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api
      .get<BriefingDTO>(`/clients/${id}/briefing`)
      .then((d) => {
        setData(d);
        setResponses(d.responses || {});
      })
      .catch(() => setData(null));
  }, [id]);

  function set(key: string) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setSaved(false);
      const val = e.target.value;
      const aliases = FIELD_ALIASES[key] || [key];
      setResponses((r) => {
        const next = { ...r, [key]: val };
        for (const a of aliases) {
          next[a] = val;
        }
        return next;
      });
    };
  }

  async function salvar() {
    setSaving(true);
    try {
      const updated = await api.put<BriefingDTO>(`/clients/${id}/briefing`, { responses });
      setData(updated);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  const hasAnyStrategicAnswer = STRATEGIC_BRIEFING_CHAPTERS.some((ch) =>
    ch.fields.some((f) => !!getBriefingValue(responses, f.key))
  );

  const hasAnyLegacyAnswer = BRIEFING_SCHEMA.some((sec) =>
    sec.fields.some((f) => (responses[f.key] ?? "").trim() !== "")
  );

  const totalAnswered = STRATEGIC_BRIEFING_CHAPTERS.reduce(
    (acc, ch) => acc + ch.fields.filter((f) => !!getBriefingValue(responses, f.key)).length,
    0
  );

  return (
    <AdminShell>
      <Link to={`/clientes/${id}`} className="type-caption text-text-tertiary">
        ← Voltar ao cliente
      </Link>
      <div className="mt-1 flex items-center gap-3">
        <h1 className="type-h1 text-text-cream">Diagnóstico Estratégico</h1>
        {data !== null && (
          <span
            className={`type-overline rounded-[var(--radius-control-sm)] px-2.5 py-0.5 ${
              data.status === "submitted"
                ? "bg-[var(--success-soft)] text-success font-medium"
                : "bg-[var(--warning-soft)] text-warning font-medium"
            }`}
          >
            {data.status === "submitted" ? `Enviado (${totalAnswered} respostas)` : "Rascunho / pendente"}
          </span>
        )}
      </div>
      <p className="type-caption mt-1 text-text-tertiary">
        Só você pode editar as respostas aqui — a cliente vê na área dela, mas não altera.
      </p>

      {data === null && <Skeleton className="mt-6 h-40" />}

      {data !== null && !hasAnyStrategicAnswer && !hasAnyLegacyAnswer && (
        <p className="type-caption mt-4 text-text-tertiary">
          A cliente ainda não respondeu nada — você já pode preencher por ela abaixo, se quiser.
        </p>
      )}

      {data !== null && (
        <div className="mt-6 flex flex-col gap-6">
          {/* Seção Principal: Os 5 Capítulos do Diagnóstico Estratégico Unik */}
          {STRATEGIC_BRIEFING_CHAPTERS.map((chapter) => (
            <Card key={chapter.id}>
              <div className="flex items-center gap-2.5 border-b border-border-subtle/80 pb-3">
                <span
                  className="h-3 w-3 rounded-full shadow-sm"
                  style={{ backgroundColor: chapter.color }}
                />
                <div>
                  <h2 className="type-h3 text-text-cream">{chapter.title}</h2>
                  <p className="type-caption text-text-tertiary">{chapter.subtitle}</p>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-4">
                {chapter.fields.map((f) => {
                  const currentVal = getBriefingValue(responses, f.key) ?? "";
                  return (
                    <TextArea
                      key={f.key}
                      label={f.label}
                      hint={f.hint}
                      rows={f.rows || 3}
                      value={currentVal}
                      onChange={set(f.key)}
                    />
                  );
                })}
              </div>
            </Card>
          ))}

          {/* Seção Legada Secundária (se houver dados preenchidos no schema antigo) */}
          {hasAnyLegacyAnswer && (
            <details className="rounded-[var(--radius-card)] border border-border-subtle bg-surface-raised p-4">
              <summary className="type-label cursor-pointer text-text-secondary hover:text-text-primary">
                Exibir perguntas do schema secundário/antigo
              </summary>
              <div className="mt-4 flex flex-col gap-6">
                {BRIEFING_SCHEMA.map((section) => (
                  <div key={section.key} className="space-y-3">
                    <h3 className="type-h3 text-text-cream">{section.title}</h3>
                    <div className="flex flex-col gap-4">
                      {section.fields.map((f) => {
                        if (f.type === "textarea") {
                          return (
                            <TextArea
                              key={f.key}
                              label={f.label}
                              value={responses[f.key] ?? ""}
                              onChange={set(f.key)}
                            />
                          );
                        }
                        if (f.type === "select") {
                          return (
                            <Select
                              key={f.key}
                              label={f.label}
                              value={responses[f.key] ?? ""}
                              onChange={set(f.key)}
                            >
                              <option value="">Selecione…</option>
                              {(f.options ?? []).map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </Select>
                          );
                        }
                        return (
                          <Input
                            key={f.key}
                            label={f.label}
                            value={responses[f.key] ?? ""}
                            onChange={set(f.key)}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          <div className="sticky bottom-4 flex items-center gap-3 rounded-[var(--radius-control)] bg-surface-overlay p-3 shadow-[var(--shadow-lg)] backdrop-blur-md border border-border-default">
            <Button onClick={salvar} loading={saving}>
              Salvar alterações
            </Button>
            {saved && <span className="type-caption text-success font-medium">✓ Salvo com sucesso!</span>}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
